<template>
  <FiltersChip
    :text="filter.text"
    :value="displayValue"
    @clear="clear"
  >
    <template #form="{ hide }">
      <v-form
        v-model="formValid"
        @submit.prevent="() => submit(hide)"
      >
        <v-select
          v-model="rangeType"
          :items="rangeItems"
          :label="`${filter.text} range`"
        />
        <template v-if="rangeType === 'custom'">
          <v-select
            v-model="timeZone"
            :items="timeZoneItems"
            label="Time zone"
          />
          <v-menu
            ref="menuAfter"
            v-model="menuAfter"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="customAfter"
                label="After"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="customAfter"
              :active-picker.sync="activePickerAfter"
              min="2020-01-01"
              :max="maxDate"
              @change="saveAfter"
            />
          </v-menu>
          <v-menu
            ref="menuBefore"
            v-model="menuBefore"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="customBefore"
                label="Before"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="customBefore"
              :active-picker.sync="activePickerBefore"
              min="2020-01-01"
              :max="maxDate"
              @change="saveBefore"
            />
          </v-menu>
        </template>
        <v-btn
          class="mt-2"
          color="primary"
          type="submit"
          small
          :disabled="!formValid"
        >
          <v-icon
            left
            small
          >
            mdi-refresh
          </v-icon>
          Apply
        </v-btn>
        <v-btn
          class="ml-2 mt-2"
          small
          @click="hide"
        >
          <v-icon
            left
            small
          >
            mdi-cancel
          </v-icon>
          Cancel
        </v-btn>
      </v-form>
    </template>
  </FiltersChip>
</template>

<script>
import FiltersChip from './FiltersChip.vue';

const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export default {
  name: 'CaseActivityFiltersDatetimeRange',
  components: {
    FiltersChip,
  },
  props: {
    filter: {
      type: Object,
      required: true,
    },
    values: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    displayValue: '',
    rangeType: 'last-day',
    customBefore: undefined,
    menuBefore: null,
    activePickerBefore: null,
    customAfter: undefined,
    menuAfter: null,
    activePickerAfter: null,
    timeZone: userTimeZone,
    rangeItems: [
      { text: 'Last 24 hours', value: 'last-day' },
      { text: 'Last 7 days', value: 'last-week' },
      { text: 'Last 30 days', value: 'last-month' },
      { text: 'Custom', value: 'custom' },
    ],
    timeZoneItems: [userTimeZone, 'UTC'],
    formValid: false,
  }),
  computed: {
    maxDate() {
      const now = (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000));
      return now.toISOString().substring(0, 10);
    },
  },
  watch: {
    menuAfter(val) {
      if (val) setTimeout(() => { this.activePickerAfter = 'YEAR'; });
    },
    menuBefore(val) {
      if (val) setTimeout(() => { this.activePickerBefore = 'YEAR'; });
    },
    values(val) {
      if (!val[`${this.filter.name}_after`] && !val[`${this.filter.name}_before`]) {
        this.customAfter = undefined;
        this.customBefore = undefined;
        this.displayValue = '';
      }
    },
  },
  created() {
    this.customAfter = this.values[`${this.filter.name}_after`];
    this.customBefore = this.values[`${this.filter.name}_before`];
    if (this.customAfter) {
      this.displayValue = `After ${this.customAfter}`;
    }
    if (this.customBefore) {
      this.displayValue = [this.displayValue, `Before ${this.customBefore}`].join(', ');
    }
  },
  methods: {
    getOffset(date, timeZone) {
      const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
      const tzDate = new Date(date.toLocaleString('en-US', { timeZone }));
      return (tzDate.getTime() - utcDate.getTime()) / 6e4;
    },
    saveAfter(date) {
      this.$refs.menuAfter.save(date);
    },
    saveBefore(date) {
      this.$refs.menuBefore.save(date);
    },
    submit(hideCallback) {
      const [values, displayValue] = this.getValues();
      this.displayValue = displayValue;
      this.$emit('apply', values);
      hideCallback();
    },
    clear() {
      this.customBefore = undefined;
      this.customAfter = undefined;
      this.displayValue = '';
      const values = {};
      values[`${this.filter.name}_after`] = undefined;
      values[`${this.filter.name}_before`] = undefined;
      this.$emit('apply', values);
    },
    getValues() {
      const values = {};
      let displayValue = '';
      let afterDate;
      let beforeDate;
      if (this.rangeType === 'custom') {
        if (this.customAfter) {
          afterDate = new Date(this.customAfter);
          afterDate.setMinutes(afterDate.getMinutes() + this.getOffset(afterDate, this.timeZone));
          displayValue = `After ${afterDate.toISOString()}`;
        }
        if (this.customBefore) {
          beforeDate = new Date(this.customBefore);
          beforeDate.setMinutes(
            beforeDate.getMinutes() + this.getOffset(beforeDate, this.timeZone),
          );
          displayValue = [displayValue, `Before ${beforeDate.toISOString()}`].join(', ');
        }
      } else {
        afterDate = new Date();
        if (this.rangeType === 'last-day') {
          afterDate.setHours(afterDate.getHours() - 24);
          displayValue = 'Last 24 hours';
        } else if (this.rangeType === 'last-week') {
          afterDate.setDate(afterDate.getDate() - 7);
          displayValue = 'Last 7 days';
        } else if (this.rangeType === 'last-month') {
          afterDate.setDate(afterDate.getDate() - 30);
          displayValue = 'Last 30 days';
        }
        values[`${this.filter.name}_after`] = afterDate.toISOString();
      }
      values[`${this.filter.name}_after`] = afterDate ? afterDate.toISOString() : undefined;
      values[`${this.filter.name}_before`] = beforeDate ? beforeDate.toISOString() : undefined;
      return [values, displayValue];
    },
  },
};
</script>
